import { useState, useEffect } from "react";
import { getAvatarUrl, getVisualUrl } from "@app/utils";

/**
 * Custom hook to handle async avatar URL loading
 *
 * @param {Object} persona - Persona object containing avatar information
 * @returns {string} - Resolved avatar URL or fallback image
 *
 * @example
 * const PersonaAvatar = ({ persona }) => {
 *   const avatarUrl = useAvatarUrl(persona);
 *   return <Avatar src={avatarUrl} alt={persona?.name} />;
 * };
 */
export function useAvatarUrl(persona) {
  const fallbackUrl =
    "https://mir-s3-cdn-cf.behance.net/project_modules/disp/690bc6105945313.5f84bfc9de488.png";
  const [avatarUrl, setAvatarUrl] = useState(fallbackUrl);

  useEffect(() => {
    let mounted = true;

    const loadAvatarUrl = async () => {
      try {
        const url = getAvatarUrl(persona);

        // Check if getAvatarUrl returned a Promise
        const resolvedUrl = url instanceof Promise ? await url : url;

        if (mounted) {
          setAvatarUrl(resolvedUrl || fallbackUrl);
        }
      } catch (error) {
        console.error("Error loading avatar URL:", error);
        if (mounted) {
          setAvatarUrl(fallbackUrl);
        }
      }
    };

    if (persona) {
      loadAvatarUrl();
    } else {
      setAvatarUrl(fallbackUrl);
    }

    return () => {
      mounted = false;
    };
  }, [persona, fallbackUrl]);

  return avatarUrl;
}

/**
 * Custom hook to handle async visual URL loading
 *
 * @param {Object} visual - Visual object containing image information
 * @returns {string} - Resolved visual URL or fallback image
 *
 * @example
 * const StoryImage = ({ visual }) => {
 *   const visualUrl = useVisualUrl(visual);
 *   return <Image src={visualUrl} alt="Story visual" />;
 * };
 */
export function useVisualUrl(visual) {
  const fallbackUrl =
    "https://react-spectrum.adobe.com/s2/preview.c3b340d3.png";
  const [visualUrl, setVisualUrl] = useState(fallbackUrl);

  useEffect(() => {
    let mounted = true;

    const loadVisualUrl = async () => {
      try {
        const url = getVisualUrl(visual);

        // Check if getVisualUrl returned a Promise
        const resolvedUrl = url instanceof Promise ? await url : url;

        if (mounted) {
          setVisualUrl(resolvedUrl || fallbackUrl);
        }
      } catch (error) {
        console.error("Error loading visual URL:", error);
        if (mounted) {
          setVisualUrl(fallbackUrl);
        }
      }
    };

    if (visual) {
      loadVisualUrl();
    } else {
      setVisualUrl(fallbackUrl);
    }

    return () => {
      mounted = false;
    };
  }, [visual, fallbackUrl]);

  return visualUrl;
}
